'use client';

import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, AlertCircle, Wallet, ShieldCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Z } from '@/lib/z-index';
import { useAnimatedClose } from '@/lib/use-animated-close';
import { useToast } from '@/lib/hooks/use-toast';
import { initiatePurchase, confirmPurchase } from '@/lib/api/skill-purchase';
import { ApiError } from '@/lib/api/client';
import type { PurchaseInitiateResp } from '@zrt-ai-office/types';
import type { ClawHubSkill } from '@/lib/clawhub-api';
import { analytics } from '@/src/utils/analytics';

interface SkillPurchaseModalProps {
	skill: ClawHubSkill;
	isOpen: boolean;
	onClose: () => void;
	/** 购买成功回调，传入更新后的 skill */
	onSuccess: (updated: ClawHubSkill) => void;
}

type ModalStep = 'loading' | 'confirm' | 'insufficient' | 'success';

export function SkillPurchaseModal({ skill, isOpen, onClose, onSuccess }: SkillPurchaseModalProps) {
	const { closing, requestClose } = useAnimatedClose(onClose, 280);
	const toast = useToast();
	const navigate = useNavigate();
	const [entered, setEntered] = useState(false);
	const [step, setStep] = useState<ModalStep>('loading');
	const [paymentInfo, setPaymentInfo] = useState<PurchaseInitiateResp | null>(null);
	const [confirming, setConfirming] = useState(false);
	// 防止同一打开周期内重复调用 initiate
	const initiatedRef = useRef(false);

	// 打开时发起 initiate 请求
	useEffect(() => {
		if (!isOpen) return;
		setEntered(false);
		setStep('loading');
		setPaymentInfo(null);
		setConfirming(false);
		initiatedRef.current = false;

		const raf = requestAnimationFrame(() => setEntered(true));
		return () => cancelAnimationFrame(raf);
	}, [isOpen]);

	useEffect(() => {
		if (!isOpen || !entered || initiatedRef.current) return;

		initiatedRef.current = true;
		const doInitiate = async () => {
			try {
				const resp = await initiatePurchase({
					skillKey: skill.skillKey ?? skill.id,
				});
				setPaymentInfo(resp);
				setStep(resp.sufficient ? 'confirm' : 'insufficient');
			} catch (err) {
				if (err instanceof ApiError) {
					const msg = err.statusText;
					if (msg.includes('已购买') || msg.includes('已经购买')) {
						toast.info('您已购买过该 Skill');
						onSuccess({ ...skill, isDownload: true });
						requestClose();
						return;
					}
					if (msg.includes('免费')) {
						toast.info('该 Skill 为免费 Skill，无需购买');
						requestClose();
						return;
					}
					toast.error(msg || '发起支付失败');
				} else {
					toast.error('网络错误，请稍后重试');
				}
				requestClose();
			}
		};

		doInitiate();
	}, [isOpen, entered, skill, toast, onSuccess, requestClose]);

	const handleConfirm = async () => {
		if (!paymentInfo || confirming) return;
		setConfirming(true);

		try {
			const result = await confirmPurchase({
				skillKey: skill.skillKey ?? skill.id,
				expectedPrice: paymentInfo.price,
			});

			// 埋点
			analytics.track('SkillPurchase', {
				skill_key: skill.skillKey ?? skill.id,
				skill_name: skill.name,
				price: result.price,
				purchase_id: result.purchaseId,
			});

			toast.success(`购买成功！已获得「${result.skillName}」`);
			setStep('success');

			// 短暂展示成功状态后关闭
			setTimeout(() => {
				onSuccess({ ...skill, isDownload: true });
				requestClose();
			}, 800);
		} catch (err) {
			if (err instanceof ApiError) {
				const msg = err.statusText;
				if (msg.includes('余额不足')) {
					setStep('insufficient');
					return;
				}
				if (msg.includes('已购买') || msg.includes('已经购买')) {
					toast.info('您已购买过该 Skill');
					onSuccess({ ...skill, isDownload: true });
					requestClose();
					return;
				}
				if (msg.includes('价格') || msg.includes('变动')) {
					toast.info('价格已变动，正在刷新...');
					// 重新发起 initiate
					try {
						const resp = await initiatePurchase({
							skillKey: skill.skillKey ?? skill.id,
						});
						setPaymentInfo(resp);
						setStep(resp.sufficient ? 'confirm' : 'insufficient');
					} catch {
						toast.error('刷新价格失败，请关闭重试');
					}
					return;
				}
				if (msg.includes('处理中') || msg.includes('稍后')) {
					toast.info('正在处理中，请稍后再试');
					return;
				}
				toast.error(msg || '支付失败');
			} else {
				toast.error('网络错误，请稍后重试');
			}
		} finally {
			setConfirming(false);
		}
	};

	const handleRecharge = () => {
		requestClose();
		navigate('/account-center');
	};

	const overlayClass = closing ? 'opacity-0 backdrop-blur-none' : entered ? 'opacity-100 backdrop-blur-xl' : 'opacity-0 backdrop-blur-none';

	const panelStyle: React.CSSProperties = {
		opacity: closing ? 0 : entered ? 1 : 0,
		transform: closing ? 'scale(0.96) translateY(8px)' : entered ? 'scale(1) translateY(0)' : 'scale(0.92) translateY(12px)',
		filter: closing ? 'blur(4px)' : entered ? 'blur(0)' : 'blur(6px)',
		transitionTimingFunction: closing ? 'cubic-bezier(0.4, 0, 1, 1)' : 'cubic-bezier(0.16, 1, 0.3, 1)',
	};

	if (!isOpen || typeof document === 'undefined') return null;

	const price = paymentInfo?.price ?? skill.price ?? 0;

	return createPortal(
		<>
			{/* Backdrop */}
			<div
				aria-hidden
				className={`liquid-glass-overlay fixed inset-0 transition-all duration-300 ease-out ${overlayClass}`}
				onClick={requestClose}
				style={{
					zIndex: Z.MODAL_NESTED,
					pointerEvents: closing || !entered ? 'none' : 'auto',
				}}
			/>

			{/* Centered modal */}
			<div
				className="fixed inset-0 flex items-center justify-center p-4"
				style={{
					zIndex: Z.MODAL_NESTED + 1,
					pointerEvents: 'none',
				}}
			>
				<div className="w-full max-w-md" style={{ ...panelStyle, pointerEvents: 'auto' }}>
					<div className="liquid-glass-modal rounded-2xl flex flex-col">
						{/* Header */}
						<div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
							<h2 className="text-base font-semibold text-foreground">购买技能</h2>
							<button
								className="flex h-7 w-7 items-center justify-center rounded-full hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
								onClick={requestClose}
							>
								<X className="h-3.5 w-3.5" />
							</button>
						</div>

						{/* Content */}
						<div className="px-6 py-5">
							{step === 'loading' && (
								<div className="flex flex-col items-center justify-center py-8 gap-3">
									<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
									<p className="text-sm text-muted-foreground">正在获取支付信息...</p>
								</div>
							)}

							{step === 'confirm' && paymentInfo && (
								<>
									{/* Skill preview */}
									<div className="liquid-glass-surface rounded-xl p-4 mb-4">
										<p className="text-sm font-medium text-foreground truncate mb-1">{paymentInfo.skillName || skill.name}</p>
										<p className="text-xs text-muted-foreground">一次付费，永久使用</p>
									</div>

									{/* Price + Balance */}
									<div className="flex items-center justify-between px-4 py-3 rounded-xl bg-secondary/50 mb-4">
										<div className="text-center flex-1">
											<p className="text-xs text-muted-foreground mb-1">技能价格</p>
											<p className="text-xl font-bold text-foreground">¥{price.toFixed(2)}</p>
										</div>
										<div className="w-px h-8 bg-border/40" />
										<div className="text-center flex-1">
											<p className="text-xs text-muted-foreground mb-1">账户余额</p>
											<p className="text-xl font-bold text-foreground">¥{paymentInfo.totalBalance.toFixed(2)}</p>
										</div>
									</div>

									{/* Trust badge */}
									<div className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground/70">
										<ShieldCheck className="h-3.5 w-3.5" />
										SafeClaw 安全支付
									</div>
								</>
							)}

							{step === 'insufficient' && paymentInfo && (
								<div className="flex flex-col items-center justify-center py-4 gap-4">
									<div className="flex items-center justify-center w-14 h-14 rounded-full bg-red-500/20">
										<AlertCircle className="text-red-500" size={28} strokeWidth={2.5} />
									</div>
									<div className="text-center">
										<p className="text-base font-semibold text-foreground mb-1">余额不足</p>
										<p className="text-sm text-muted-foreground">
											需要 ¥{price.toFixed(2)}，当前余额 ¥{paymentInfo.totalBalance.toFixed(2)}
										</p>
									</div>
								</div>
							)}

							{step === 'success' && (
								<div className="flex flex-col items-center justify-center py-6 gap-3">
									<div className="flex items-center justify-center w-14 h-14 rounded-full bg-emerald-500/20">
										<ShieldCheck className="text-emerald-500" size={28} strokeWidth={2.5} />
									</div>
									<p className="text-base font-semibold text-foreground">购买成功</p>
								</div>
							)}
						</div>

						{/* Footer */}
						<div className="px-6 py-4 border-t border-border shrink-0">
							{step === 'loading' && (
								<button
									className="w-full h-9 flex items-center justify-center rounded-xl bg-secondary hover:bg-secondary/80 text-foreground text-sm font-medium transition-colors"
									onClick={requestClose}
								>
									取消
								</button>
							)}

							{step === 'confirm' && (
								<button
									className="w-full h-10 flex items-center justify-center gap-2 rounded-xl bg-primary text-primary-foreground text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
									disabled={confirming}
									onClick={handleConfirm}
								>
									{confirming ? (
										<>
											<Loader2 className="h-4 w-4 animate-spin" />
											支付中...
										</>
									) : (
										<>
											<Wallet className="h-4 w-4" />
											确认支付 ¥{price.toFixed(2)}
										</>
									)}
								</button>
							)}

							{step === 'insufficient' && (
								<div className="flex flex-col gap-2">
									<button
										className="w-full h-10 flex items-center justify-center gap-2 rounded-xl bg-primary text-primary-foreground text-sm font-semibold transition-colors"
										onClick={handleRecharge}
									>
										<Wallet className="h-4 w-4" />
										去充值
									</button>
									<button
										className="w-full h-9 flex items-center justify-center rounded-xl bg-secondary hover:bg-secondary/80 text-foreground text-sm font-medium transition-colors"
										onClick={requestClose}
									>
										取消
									</button>
								</div>
							)}

							{step === 'success' && (
								<button
									className="w-full h-9 flex items-center justify-center rounded-xl bg-secondary hover:bg-secondary/80 text-foreground text-sm font-medium transition-colors"
									onClick={requestClose}
								>
									完成
								</button>
							)}
						</div>
					</div>
				</div>
			</div>
		</>,
		document.body,
	);
}
